<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Reports</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">File Register</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer">
        <div class="view-item position-relative">
          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <h1 class="mb-0">File Register Report</h1>
            </div>

            <div class="form-body">
              <div class="mb-3">
                <div class="report-header">
                  <h3 class="mb-3">Select Period</h3>
                </div>

                <div class="date-range-wrapper">
                  <div class="date-range-section">
                    <h5 class="range-label">From</h5>
                    <div class="dropdowns-row">
                      <div class="dropdown-container">
                        <label class="sr-only" for="startMonthSelect">Select start month</label>
                        <div class="search-box">
                          <span class="material-symbols-outlined calendar-icon">calendar_month</span>
                          <select
                            id="startMonthSelect"
                            v-model="selectedStartMonth"
                            class="form-control form-control-sm dropdown-input"
                            aria-label="Select start month"
                          >
                            <option v-for="m in months" :key="m.value" :value="m.value">
                              {{ m.label }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="dropdown-container">
                        <label class="sr-only" for="startYearSelect">Select start year</label>
                        <div class="search-box">
                          <span class="material-symbols-outlined calendar-icon">calendar_today</span>
                          <select
                            id="startYearSelect"
                            v-model="selectedStartYear"
                            class="form-control form-control-sm dropdown-input"
                            aria-label="Select start year"
                          >
                            <option v-for="y in years" :key="y" :value="y">
                              {{ y }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="date-range-section">
                    <h5 class="range-label">To</h5>
                    <div class="dropdowns-row">
                      <div class="dropdown-container">
                        <label class="sr-only" for="endMonthSelect">Select end month</label>
                        <div class="search-box">
                          <span class="material-symbols-outlined calendar-icon">calendar_month</span>
                          <select
                            id="endMonthSelect"
                            v-model="selectedEndMonth"
                            class="form-control form-control-sm dropdown-input"
                            aria-label="Select end month"
                          >
                            <option v-for="m in months" :key="m.value" :value="m.value">
                              {{ m.label }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="dropdown-container">
                        <label class="sr-only" for="endYearSelect">Select end year</label>
                        <div class="search-box">
                          <span class="material-symbols-outlined calendar-icon">calendar_today</span>
                          <select
                            id="endYearSelect"
                            v-model="selectedEndYear"
                            class="form-control form-control-sm dropdown-input"
                            aria-label="Select end year"
                          >
                            <option v-for="y in years" :key="y" :value="y">
                              {{ y }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div v-if="!loadingRegister" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <!-- <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search file registers..." />
                    </div> -->
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to Excel</a>
                  </div>
                </div>
              </div>

              <div class="table-container">
                <div v-if="loadingRegister" class="loading-state text-center py-5">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading file register...</p>
                </div>

                <table v-else class="register-table">
                  <thead>
                    <tr>
                      <th>ENTRY DATE</th>
                      <th>NUMBER OF ENTRIES</th>
                      <th>DETAILS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="paginatedItems.length === 0">
                      <td colspan="3" class="text-center">No file registers found for this period</td>
                    </tr>

                    <tr
                      v-for="(register, idx) in paginatedItems"
                      :key="idx"
                      :class="idx % 2 === 0 ? 'row-even' : 'row-odd'"
                      v-else
                    >
                      <td data-label="ENTRY DATE">{{ formatDate(register.entryDate) }}</td>
                      <td data-label="NUMBER OF FILES" class="text-center">
                        <span class="clickable-number" @click="openModal(register)">
                          {{ register.cumulativeFileCount }}
                        </span>
                      </td>
                      <td data-label="ACTION" class="text-center">
                        <button
                          class="btn btn-sm btn-view-details"
                          @click="openModal(register)"
                          title="View register details"
                        >
                          <span class="material-symbols-outlined">visibility</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loadingRegister" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in getPageNumbers" :key="page" class="page-item"
                      :class="{ active: currentPage === page, disabled: page === '...' }">
                      <button class="page-link" @click.prevent="page !== '...' && changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Popup -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="mb-0">Register Files</h3>
          <button class="close-btn" @click="closeModal" aria-label="Close modal">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="selectedRegister">
            <div class="register-files-list">
              <div class="register-summary">
                <div class="summary-item">
                  <span class="summary-label">Entry Date:</span>
                  <span class="summary-value">{{ formatDate(selectedRegister.entryDate) }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total Files:</span>
                  <span class="summary-value">{{ modalFiles.length }}</span>
                </div>
              </div>

              <div v-if="modalFiles.length === 0" class="text-center py-4">
                <span class="material-symbols-outlined empty-icon">folder_open</span>
                <p class="mt-2">No files in this register.</p>
              </div>

              <div v-else class="files-table-wrapper">
              <table class="files-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ENTRY DATE</th>
                    <th>QUEUE #</th>
                    <th>FILE NUMBER</th>
                    <th>FILE TITLE</th>
                    <th>MDA</th>
                    <th>OFFICE</th>
                    <th>ENTITY</th>
                    <th>AMOUNT</th>
                    <th>DAYS SPENT</th>
                    <th>ACTION DATE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(file, idx) in modalFiles" :key="idx">
                    <td data-label="#">{{ idx + 1 }}</td>
                    <td data-label="ENTRY DATE">{{ formatDate(file.entryDate) }}</td>
                    <td data-label="QUEUE #">{{ file.queueNumber }}</td>
                    <td data-label="FILE NUMBER">{{ file.fileNumber }}</td>
                    <td data-label="FILE TITLE">{{ file.fileTitle }}</td>
                    <td data-label="MDA">{{ file.mda }}</td>
                    <td data-label="OFFICE">{{ file.office }}</td>
                    <td data-label="ENTITY TYPE">
                      <span class="entity-badge" :class="'badge-' + file.entityType.toLowerCase()">
                        {{ file.entityType }}
                      </span>
                    </td>
                    <td data-label="AMOUNT" class="text-right">₦{{ formatAmount(file.amount) }}</td>
                    <td data-label="DAYS SPENT" class="text-center">{{ file.numOfDaysSpent }}</td>
                    <td data-label="ACTION DATE">{{ formatDate(file.actionDate) }}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-secondary">
            <span class="material-symbols-outlined">close</span>
            Close
          </button>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { debounce } from 'lodash'
import ExcelJS from 'exceljs'

const root_api_url = process.env.VUE_APP_API_ROOT_URL || ''
const now = new Date()
const pad = (n) => String(n).padStart(2, '0')

// Initialize with current month/year and previous month/year
const selectedStartMonth = ref(now.getMonth() === 0 ? 12 : now.getMonth())
const selectedStartYear = ref(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear())
const selectedEndMonth = ref(now.getMonth() + 1)
const selectedEndYear = ref(now.getFullYear())

const registers = ref([])
const loadingRegister = ref(true)

// Pagination and search
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const debouncedSearch = ref('')

watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

// Modal state
const showModal = ref(false)
const selectedRegister = ref(null)

// Flatten every file for the selected date, tagging each with its register's entry date
const modalFiles = computed(() => {
  if (!selectedRegister.value) return []
  return selectedRegister.value.sourceRegisters.flatMap(tracker =>
    tracker.miniRegisters.map(file => ({ ...file, entryDate: tracker.entryDate }))
  )
})

// Generate months array
const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
]

// Generate years array (current year and 5 years back)
const years = computed(() => {
  const currentYear = now.getFullYear()
  const yearsList = []
  for (let i = 0; i <= 5; i++) {
    yearsList.push(currentYear - i)
  }
  return yearsList
})

const getEntryDateKey = (entryDate) => {
  if (!entryDate) return 'invalid'
  const d = new Date(entryDate)
  if (isNaN(d.getTime())) return 'invalid'
  const yyyy = d.getUTCFullYear()
  const mm = pad(d.getUTCMonth() + 1)
  const dd = pad(d.getUTCDate())
  return `${yyyy}-${mm}-${dd}`
}

const groupedRegisters = computed(() => {
  const map = new Map()

  // Flatten nested arrays (e.g. API returns [[obj1, obj2]] — double-wrapped)
  const flatRegisters = registers.value.flat(Infinity).filter(
    r => r && typeof r === 'object' && !Array.isArray(r)
  )

  flatRegisters.forEach(register => {
    const key = getEntryDateKey(register.entryDate)
    if (!map.has(key)) {
      map.set(key, {
        entryDate: register.entryDate,
        miniRegisters: [],
        totalNoOfDaysSpent: 0,
        sourceRegisters: []
      })
    }

    const group = map.get(key)
    group.miniRegisters = group.miniRegisters.concat(register.miniRegisters || [])
    group.totalNoOfDaysSpent += Number(register.totalNoOfDaysSpent || 0)
    group.sourceRegisters.push({
      entryDate: register.entryDate,
      totalNoOfDaysSpent: Number(register.totalNoOfDaysSpent || 0),
      fileCount: (register.miniRegisters || []).length,
      miniRegisters: register.miniRegisters || []
    })
  })

  const sorted = Array.from(map.values()).sort((a, b) => new Date(a.entryDate) - new Date(b.entryDate))

  // Build cumulative file count from the first entry (count of source registers per date group)
  let cumulative = 0
  sorted.forEach(group => {
    cumulative += group.sourceRegisters.length
    group.cumulativeFileCount = cumulative
  })

  return sorted
})

const filteredItems = computed(() => {
  return groupedRegisters.value.filter(register => {
    const term = debouncedSearch.value.toLowerCase()
    return (
      formatDate(register.entryDate).toLowerCase().includes(term) ||
      register.miniRegisters.length.toString().includes(term) ||
      register.totalNoOfDaysSpent.toString().includes(term)
    )
  })
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const getPageNumbers = computed(() => {
  const pages = [];
  const delta = 2;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1); // always show first

  if (current > delta + 2) pages.push('...');

  const start = Math.max(2, current - delta);
  const end = Math.min(total - 1, current + delta);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - delta - 1) pages.push('...');

  pages.push(total); // always show last

  return pages;
});

const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0)
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  if (!(date instanceof Date)) date = new Date(date)
  if (isNaN(date.getTime())) return 'N/A'
  const mm = pad(date.getMonth() + 1)
  const dd = pad(date.getDate())
  const yyyy = date.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

// const createEntityLink = (register) => {
//   if (!register || !register.miniRegisters || register.miniRegisters.length === 0) return null
  
//   const firstFile = register.miniRegisters[0]
//   if (!firstFile.entityType || !firstFile.entityId) return null

//   const entityType = firstFile.entityType.toLowerCase()
//   if (entityType === 'memo') {
//     return { name: 'ViewMemo', params: { id: firstFile.entityId } }
//   } else if (entityType === 'appraisal') {
//     return { name: 'ViewAppraisal', params: { id: firstFile.entityId } }
//   }
//   return null
// }

async function fetchFileRegisters(startMonth, startYear, endMonth, endYear) {
  const url = `${root_api_url}/appraisal/fileRegister/all/${startMonth}/${startYear}/${endMonth}/${endYear}`

  const resolveRegisterObject = (register) => {
    if (!register || typeof register !== 'object') return null
    if (Array.isArray(register.miniRegisters)) return register

    const numericKeys = Object.keys(register).filter(key => !isNaN(Number(key))).sort((a, b) => Number(a) - Number(b))
    for (const key of numericKeys) {
      const nested = resolveRegisterObject(register[key])
      if (nested) return nested
    }

    const wrappers = ['data', 'content', 'payload', 'result', 'results', 'items', 'registers']
    for (const key of wrappers) {
      const nested = resolveRegisterObject(register[key])
      if (nested) return nested
    }

    return null
  }

  const normalizeRegister = (register) => {
    const resolved = resolveRegisterObject(register) || register
    const miniRegisters = Array.isArray(resolved.miniRegisters) ? resolved.miniRegisters : []
    return {
      entryDate: resolved.entryDate,
      totalNoOfDaysSpent: resolved.totalNoOfDaysSpent || 0,
      miniRegisters: miniRegisters.map(item => ({
        id: item.id,
        version: item.version,
        entityType: item.entityType || '',
        entityId: item.entityId,
        parentId: item.parentId,
        queueNumber: item.queueNumber || 0,
        office: item.office || '',
        mda: item.mda || '',
        fileTitle: item.fileTitle || '',
        fileNumber: item.fileNumber || '',
        amount: item.amount || 0,
        createdDate: item.createdDate,
        actionDate: item.actionDate,
        groupInvolved: item.groupInvolved || '',
        actionTaken: item.actionTaken || '',
        numOfDaysSpent: item.numOfDaysSpent || 0,
        fileTrackerNumber: item.fileTrackerNumber
      }))
    }
  }

  const extractRegisterArray = (payload) => {
    if (!payload) return []

    const wrappers = ['data', 'content', 'payload', 'result', 'results', 'items', 'registers']
    const queue = [payload]
    const extracted = []

    while (queue.length > 0) {
      const current = queue.shift()
      if (!current) continue

      if (Array.isArray(current)) {
        queue.push(...current)
        continue
      }

      if (typeof current !== 'object') {
        continue
      }

      if (Array.isArray(current.miniRegisters)) {
        extracted.push(current)
        continue
      }

      let pushed = false

      wrappers.forEach((key) => {
        if (current[key] !== undefined && current[key] !== null) {
          queue.push(current[key])
          pushed = true
        }
      })

      const numericKeys = Object.keys(current).filter(key => !isNaN(Number(key)))
      if (numericKeys.length > 0) {
        queue.push(...numericKeys.sort((a, b) => Number(a) - Number(b)).map(key => current[key]))
        pushed = true
      }

      if (!pushed) {
        Object.values(current).forEach((value) => {
          if (value && (typeof value === 'object' || Array.isArray(value))) {
            queue.push(value)
          }
        })
      }
    }

    return extracted
  }

  try {
    const resp = await axios.get(url)
    const data = resp.data ?? []
    const dataArray = extractRegisterArray(data)

    return dataArray.map(normalizeRegister).filter(r => r.entryDate || r.miniRegisters.length > 0)
  } catch (err) {
    console.error('API fetch failed', err)
    return []
  }
}

function openModal(register) {
  selectedRegister.value = register
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  selectedRegister.value = null
}

async function exportToExcel() {
  const getMonthName = (monthNum) => months.find(m => m.value === monthNum)?.label ?? monthNum

  const registersList = registers.value
    .flat(Infinity)
    .filter(item => item && typeof item === 'object' && !Array.isArray(item))

  const allFiles = registersList.flatMap(register => Array.isArray(register.miniRegisters) ? register.miniRegisters : [])
  if (allFiles.length === 0) return

  const byTracker = new Map()
  allFiles.forEach(file => {
    const tracker = file.fileTrackerNumber || `unknown-${file.id || ''}`
    if (!byTracker.has(tracker)) byTracker.set(tracker, [])
    byTracker.get(tracker).push(file)
  })

  const stageSet = new Set()
  const stages = []
  byTracker.forEach(files => {
    const ordered = [...files].sort((a, b) => Number(a.queueNumber || 0) - Number(b.queueNumber || 0))
    ordered.forEach(file => {
      const officeName = String(file.office || 'UNKNOWN').trim() || 'UNKNOWN'
      const queueNo = Number(file.queueNumber || 0)
      const stageKey = `${queueNo}__${officeName.toUpperCase()}`
      if (!stageSet.has(stageKey)) {
        stageSet.add(stageKey)
        stages.push({ stageKey, queueNo, officeName })
      }
    })
  })
  stages.sort((a, b) => a.queueNo - b.queueNo)

  const officeOccurrence = {}
  const stageHeaders = stages.map(stage => {
    const office = stage.officeName.toUpperCase()
    officeOccurrence[office] = (officeOccurrence[office] || 0) + 1
    const occ = officeOccurrence[office]
    return {
      ...stage,
      stageLabel: occ > 1 ? `${office} (${occ})` : office
    }
  })

  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('File Register')

  const baseCols = ['S/N', 'MDAs', 'FILE TITLE', 'FILE NUMBER', 'AMOUNT (N)']
  const flowCols = ['DATE RECEIVED', 'ACTION TAKEN', 'ACTION DATE', 'NUMBER OF DAYS SPENT']

  const totalCols = baseCols.length + (stageHeaders.length * flowCols.length)

  const title = `FILE REGISTER REPORT - PERIOD: ${getMonthName(selectedStartMonth.value)} ${selectedStartYear.value} TO ${getMonthName(selectedEndMonth.value)} ${selectedEndYear.value}`
  sheet.mergeCells(1, 1, 1, totalCols)
  sheet.getCell(1, 1).value = title
  sheet.getCell(1, 1).font = { bold: true }
  sheet.getCell(1, 1).alignment = { horizontal: 'center', vertical: 'middle' }

  const groupRow = 2
  const headRow = 3

  baseCols.forEach((name, idx) => {
    const col = idx + 1
    sheet.getCell(headRow, col).value = name
    sheet.getCell(headRow, col).font = { bold: true }
    sheet.getCell(headRow, col).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  })

  let colPtr = baseCols.length + 1
  stageHeaders.forEach(stage => {
    const start = colPtr
    const end = colPtr + flowCols.length - 1

    sheet.mergeCells(groupRow, start, groupRow, end)
    sheet.getCell(groupRow, start).value = stage.stageLabel
    sheet.getCell(groupRow, start).font = { bold: true }
    sheet.getCell(groupRow, start).alignment = { horizontal: 'center', vertical: 'middle' }

    flowCols.forEach((label, i) => {
      const col = start + i
      sheet.getCell(headRow, col).value = label
      sheet.getCell(headRow, col).font = { bold: true }
      sheet.getCell(headRow, col).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    })

    colPtr = end + 1
  })

  const trackerRows = Array.from(byTracker.entries())
    .map(([fileTrackerNumber, files]) => ({
      fileTrackerNumber,
      files: [...files].sort((a, b) => Number(a.queueNumber || 0) - Number(b.queueNumber || 0))
    }))
    .sort((a, b) => Number(a.files[0]?.queueNumber || 0) - Number(b.files[0]?.queueNumber || 0))

  trackerRows.forEach((tracker, index) => {
    const files = tracker.files
    const first = files[0] || {}

    const row = [
      index + 1,
      first.mda || '',
      first.fileTitle || files.find(x => x.fileTitle)?.fileTitle || '',
      first.fileNumber || files.find(x => x.fileNumber)?.fileNumber || '',
      Number(first.amount || 0)
    ]

    const stageData = new Map()
    files.forEach(file => {
      const officeName = String(file.office || 'UNKNOWN').trim() || 'UNKNOWN'
      const queueNo = Number(file.queueNumber || 0)
      const key = `${queueNo}__${officeName.toUpperCase()}`
      if (!stageData.has(key)) stageData.set(key, file)
    })

    stageHeaders.forEach(stage => {
      const data = stageData.get(stage.stageKey)
      if (!data) {
        row.push('', '', '', '')
      } else {
        row.push(
          formatDate(data.createdDate),
          data.actionTaken || '',
          formatDate(data.actionDate),
          Number(data.numOfDaysSpent || 0)
        )
      }
    })

    sheet.addRow(row)
  })

  for (let col = 1; col <= totalCols; col++) {
    let width = 16
    if (col === 1) width = 8
    if (col === 2) width = 24
    if (col === 3) width = 28
    if (col === 4) width = 22
    if (col === 5) width = 14
    sheet.getColumn(col).width = width
  }

  const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }
  const groupFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB7DEE8' } }

  for (let col = 1; col <= totalCols; col++) {
    sheet.getCell(groupRow, col).fill = groupFill
    sheet.getCell(headRow, col).fill = headerFill
  }

  for (let row = 2; row <= sheet.rowCount; row++) {
    for (let col = 1; col <= totalCols; col++) {
      const cell = sheet.getCell(row, col)
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
      if (row >= 4) {
        cell.alignment = {
          vertical: 'middle',
          horizontal: col === 1 ? 'center' : 'left',
          wrapText: true
        }
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `file-register-${getMonthName(selectedStartMonth.value)}-${selectedStartYear.value}-to-${getMonthName(selectedEndMonth.value)}-${selectedEndYear.value}.xlsx`
  link.click()
  URL.revokeObjectURL(url)
}

let ongoingFetchToken = 0

async function loadData() {
  loadingRegister.value = true
  registers.value = []
  const localToken = ++ongoingFetchToken
  try {
    const fetchedRegisters = await fetchFileRegisters(
      selectedStartMonth.value,
      selectedStartYear.value,
      selectedEndMonth.value,
      selectedEndYear.value
    )
    if (localToken !== ongoingFetchToken) return
    registers.value = fetchedRegisters
  } catch (err) {
    console.error('Load data error', err)
    registers.value = []
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${err?.response?.data?.errorMessage || err?.response?.data?.error || err?.response?.data?.message || 'Failed to load file register. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  } finally {
    if (localToken === ongoingFetchToken) {
      loadingRegister.value = false
    }
  }
}

watch([selectedStartMonth, selectedStartYear, selectedEndMonth, selectedEndYear], loadData)

watch(pageLength, () => {
  currentPage.value = 1
})

onMounted(async () => {
  await loadData()

  document.title = 'File Register Report'
})
</script>

<style scoped>
@import '@/assets/css/table.css';
@import '@/assets/css/responsive-table.css';

/* Header Section */
.report-header {
  margin-bottom: 16px;
}

.report-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Date Range Wrapper */
.date-range-wrapper {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.date-range-section {
  flex: 1;
  min-width: 300px;
}

.range-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dropdowns-row {
  display: flex;
  gap: 12px;
}

.dropdown-container {
  flex: 1;
  min-width: 140px;
}

.search-box {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
}

.calendar-icon {
  position: absolute;
  left: 10px;
  font-size: 1.1rem;
  opacity: 0.8;
  pointer-events: none;
  z-index: 1;
}

.dropdown-input {
  padding-left: 38px;
  padding-right: 12px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid #d0d7de;
  background: #fff;
  font-size: 0.9rem;
  width: 100%;
  cursor: pointer;
}

.dropdown-input:focus {
  outline: none;
  border-color: #0969da;
  box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
}

/* Table Styles */
.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.register-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.register-table thead th {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e6e6e6;
  background: #fafafa;
  font-weight: 600;
  white-space: nowrap;
}

.register-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.register-table .text-right {
  text-align: right;
}

.register-table .text-center {
  text-align: center;
}

.row-even {
  background: #ffffff;
}

.row-odd {
  background: #fbfbfb;
}

.row-even:hover,
.row-odd:hover {
  background: #f0f7ff;
  transition: background 0.2s;
}

/* Entity Badge */
.entity-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
}

.badge-memo {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-appraisal {
  background: #f3e5f5;
  color: #7b1fa2;
}

/* Button Styles */
.btn-view-details {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #0969da;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-view-details:hover {
  background-color: #033d8b;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(9, 105, 218, 0.3);
  color: white;
}

.btn-view-details .material-symbols-outlined {
  font-size: 1rem;
}

/* Modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in-out;
  padding: 16px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
  background: #f8f9fa;
}

.modal-header h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #666;
  transition: all 0.2s;
  border-radius: 4px;
  flex-shrink: 0;
}

.close-btn:hover {
  color: #000;
  background: #e9ecef;
}

.modal-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #e6e6e6;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #f8f9fa;
}

/* Register Details */
.register-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.register-files-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.register-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  padding: 16px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #f0f7ff;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.summary-value {
  font-size: 1.05rem;
  font-weight: 600;
  color: #0969da;
}

/* Files Table */
.files-table-wrapper {
  overflow-x: auto;
}

.files-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.files-table thead th {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e6e6e6;
  background: #fafafa;
  font-weight: 600;
  white-space: nowrap;
  font-size: 0.85rem;
}

.files-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.files-table .text-right {
  text-align: right;
}

.files-table .text-center {
  text-align: center;
}

.details-section {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e6e6e6;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.detail-value {
  font-size: 0.95rem;
  color: #333;
  word-break: break-word;
  padding: 4px 8px;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #0969da;
  padding-left: 12px;
}

/* Entity Box */
.entity-box {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: white;
}

.entity-type-badge {
  padding: 8px 16px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  flex-shrink: 0;
  height: fit-content;
}

.badge-memo {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-appraisal {
  background: #f3e5f5;
  color: #7b1fa2;
}

.entity-info {
  flex: 1;
}

.entity-id {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 12px;
  word-break: break-all;
}

.entity-id strong {
  color: #333;
}

.btn-view-entity {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
}

.btn-view-entity:hover {
  background-color: #218838;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

.btn-view-entity .material-symbols-outlined {
  font-size: 1.1rem;
}

/* Clickable numbers styling */
.clickable-number {
  color: #0969da;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  font-weight: 500;
  display: inline-block;
}

.clickable-number:hover {
  background: #ddf4ff;
  text-decoration: underline;
}

/* Button Styles */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-outline-back {
  background-color: transparent;
  color: #0969da;
  border: 1px solid #0969da;
}

.btn-outline-back:hover {
  background-color: #f0f7ff;
}

.btn .material-symbols-outlined {
  font-size: 1.1rem;
}

/* Loading spinner */
.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

.sr-only {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .date-range-wrapper {
    flex-direction: column;
    gap: 20px;
  }

  .date-range-section {
    min-width: auto;
  }

  .dropdowns-row {
    flex-direction: column;
  }

  .dropdown-container {
    min-width: auto;
  }

  /* Convert table to card layout on mobile */
  .register-table {
    font-size: 0.85rem;
  }

  .register-table thead {
    display: none;
  }

  .register-table tbody,
  .register-table tfoot {
    display: block;
  }

  .register-table tbody tr {
    display: block;
    margin-bottom: 12px;
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    padding: 12px;
    background: white;
  }

  .register-table tbody tr td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    text-align: right;
  }

  .register-table tbody tr td:last-child {
    border-bottom: none;
  }

  .register-table tbody tr td::before {
    content: attr(data-label);
    font-weight: 600;
    text-align: left;
    color: #333;
  }

  /* Files table in modal */
  .files-table thead {
    display: none;
  }

  .files-table tbody tr {
    display: block;
    margin-bottom: 12px;
    border: 1px solid #e6e6e6;
    border-radius: 6px;
    padding: 10px;
    background: white;
  }

  .files-table tbody tr td {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 6px 0;
    border-bottom: 1px solid #f0f0f0;
    text-align: right;
    font-size: 0.85rem;
  }

  .files-table tbody tr td:last-child {
    border-bottom: none;
  }

  .files-table tbody tr td::before {
    content: attr(data-label);
    font-weight: 600;
    text-align: left;
    color: #333;
    flex: 0 0 100px;
  }

  /* Modal adjustments for mobile */
  .modal-overlay {
    padding: 8px;
  }

  .modal-content {
    max-height: 90vh;
  }

  .modal-header {
    padding: 12px 16px;
  }

  .modal-header h3 {
    font-size: 1rem;
  }

  .modal-body {
    padding: 12px 16px;
  }

  .register-summary {
    grid-template-columns: 1fr;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .entity-box {
    flex-direction: column;
  }
}

/* Tablet Responsive Styles */
@media (min-width: 769px) and (max-width: 1024px) {
  .date-range-wrapper {
    gap: 24px;
  }

  .register-table {
    font-size: 0.9rem;
  }

  .register-table thead th,
  .register-table tbody td {
    padding: 8px 10px;
  }

  .files-table thead th,
  .files-table tbody td {
    padding: 8px 10px;
  }

  .modal-content {
    max-width: 90%;
  }

  .register-summary {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
